/**
 * Modal Shell - per `Design/Mockups/_Notes - Login Modal.md` and
 * `Design/Mockups/_Notes - Share Modal.md`.
 *
 * 380px wide (Login Modal mockup width; Share Modal at 360px is achievable
 * by setting the wrapper width via CSS overrides). 16px radius, 24px padding,
 * 1px border, `card` fill. Header has a title, optional subtitle, and a
 * close X button. Content slot is provided by the caller.
 *
 * PM 2026-06-10 bug pass: the shell now registers itself with the shared
 * `modal-stack` so ESC/z-index ordering is centralised. Callers can pass
 * `tier` (default `'modal'`) and `singletonKey` to opt into the singleton
 * behaviour required by the Login Modal.
 */

import { iconSvg } from './icons';
import { registerModal, type ModalStackEntry, type ModalTier } from './modal-stack';

export interface ModalShellProps {
  title: string;
  subtitle?: string;
  /** When true (default), clicking the backdrop dismisses the modal. */
  closeOnBackdrop?: boolean;
  onClose: () => void;
  /** Content node inserted into the modal body. */
  content: Node;
  /**
   * Override shell width in pixels. Defaults to 380 (Login Modal spec).
   * Share Modal consumer passes `360` per `_Notes - Share Modal.md`.
   * Phase 1.13 resolved the 380 vs 360 drift by making it per-surface.
   */
  width?: number;
  /**
   * Stack tier. Defaults to `'modal'`. The Login Modal passes `'auth'` so
   * it always paints above any open end-game overlay (bug E fix).
   */
  tier?: ModalTier;
  /**
   * Singleton key. If supplied, opening a second instance is a no-op and
   * `mountModalShell` returns a handle whose `update`/`destroy` proxy to
   * the existing instance. The Login Modal uses `'auth'` (bug H fix).
   */
  singletonKey?: string;
}

export interface ModalHandle {
  /** Re-render with new props. */
  update: (props: ModalShellProps) => void;
  /** Remove the modal from the DOM and clean up listeners. */
  destroy: () => void;
  /**
   * True when this handle proxies an already-mounted singleton instance
   * instead of having mounted a fresh shell. Consumers should not mount
   * their own DOM content when this is true.
   */
  readonly isExistingSingleton: boolean;
}

interface InternalRefs {
  backdrop: HTMLDivElement;
  shell: HTMLDivElement;
  title: HTMLHeadingElement;
  subtitle: HTMLParagraphElement;
  closeBtn: HTMLButtonElement;
  body: HTMLDivElement;
}

function buildSkeleton(): InternalRefs {
  const backdrop = document.createElement('div');
  backdrop.className = 'gv-modal-backdrop';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');

  const shell = document.createElement('div');
  shell.className = 'gv-modal-shell';
  backdrop.appendChild(shell);

  const header = document.createElement('div');
  header.className = 'gv-modal-header';

  const titles = document.createElement('div');
  titles.className = 'gv-modal-titles';

  const title = document.createElement('h2');
  title.className = 'gv-modal-title';
  titles.appendChild(title);

  const subtitle = document.createElement('p');
  subtitle.className = 'gv-modal-subtitle';
  titles.appendChild(subtitle);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'gv-modal-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.appendChild(iconSvg('x', 18));

  header.append(titles, closeBtn);
  shell.appendChild(header);

  const body = document.createElement('div');
  body.className = 'gv-modal-content';
  shell.appendChild(body);

  return { backdrop, shell, title, subtitle, closeBtn, body };
}

const NOOP_HANDLE: ModalHandle = {
  update: (): void => {},
  destroy: (): void => {},
  isExistingSingleton: true,
};

export function mountModalShell(target: HTMLElement, props: ModalShellProps): ModalHandle {
  const refs = buildSkeleton();
  let current = props;

  function onBackdropClick(e: MouseEvent): void {
    if ((current.closeOnBackdrop ?? true) && e.target === refs.backdrop) {
      current.onClose();
    }
  }
  function onCloseClick(): void {
    current.onClose();
  }

  refs.backdrop.addEventListener('click', onBackdropClick);
  refs.closeBtn.addEventListener('click', onCloseClick);

  function render(p: ModalShellProps): void {
    current = p;
    refs.title.textContent = p.title;
    refs.title.id = `gv-modal-${slug(p.title)}`;
    refs.backdrop.setAttribute('aria-labelledby', refs.title.id);

    if (p.subtitle !== undefined && p.subtitle.length > 0) {
      refs.subtitle.textContent = p.subtitle;
      refs.subtitle.hidden = false;
    } else {
      refs.subtitle.textContent = '';
      refs.subtitle.hidden = true;
    }

    const widthPx = p.width ?? 380;
    refs.shell.style.width = `${widthPx}px`;

    refs.body.replaceChildren(p.content);
  }

  render(props);
  target.appendChild(refs.backdrop);

  const entry: ModalStackEntry = registerModal({
    element: refs.backdrop,
    tier: props.tier ?? 'modal',
    onClose: (): void => current.onClose(),
    dismissOnEsc: true,
    dismissOnBackdrop: props.closeOnBackdrop ?? true,
    ...(props.singletonKey !== undefined ? { singletonKey: props.singletonKey } : {}),
  });

  // Singleton collision: another caller already mounted this surface. Tear
  // down the skeleton we just built and return a no-op handle so the caller
  // does not attempt to drive a phantom UI. The existing entry has already
  // pulled focus per `registerModal`.
  if (entry.isExisting) {
    refs.backdrop.removeEventListener('click', onBackdropClick);
    refs.closeBtn.removeEventListener('click', onCloseClick);
    refs.backdrop.remove();
    return NOOP_HANDLE;
  }

  return {
    isExistingSingleton: false,
    update: (next: ModalShellProps): void => {
      render(next);
    },
    destroy: (): void => {
      refs.backdrop.removeEventListener('click', onBackdropClick);
      refs.closeBtn.removeEventListener('click', onCloseClick);
      entry.release();
      refs.backdrop.remove();
    },
  };
}

function slug(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
